"use client";

import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatDateLong, formatTimeRange } from "@/lib/format/date";
import {
  Package,
  MapPin,
  Clock,
  User,
  Phone,
  ExternalLink,
  Eye,
  Hand,
  Truck,
  RotateCcw,
  ArrowUpFromLine,
  ShieldCheck,
} from "lucide-react";

interface ShipmentCardProps {
  shipment: {
    id: string;
    type: string;
    status: string;
    otpVerifiedAt?: string | null;
    slot: {
      startAt: string;
      endAt: string;
    };
    order: {
      id: string;
      publicId: string;
      status: string;
      orderType: string;
      itemCount: number;
    };
    customer: {
      publicId: string;
      name: string;
      phone?: string | null;
    };
    address: {
      line1: string;
      line2?: string | null;
      city: string;
      state: string;
      pincode: string;
      lat?: number | null;
      lng?: number | null;
    };
    area: {
      name: string;
    };
    assignedRider?: {
      name: string;
    } | null;
  };
  onClaim?: () => void;
  onOpen?: () => void;
  isClaiming?: boolean;
}

const typeConfig: Record<string, { icon: React.ElementType; label: string; color: string }> = {
  PICKUP: { icon: ArrowUpFromLine, label: "Pickup", color: "bg-blue-100 text-blue-700" },
  DELIVERY: { icon: Truck, label: "Delivery", color: "bg-green-100 text-green-700" },
  RETURN: { icon: RotateCcw, label: "Return", color: "bg-orange-100 text-orange-700" },
  REWORK: { icon: RotateCcw, label: "Rework", color: "bg-amber-100 text-amber-700" },
};

const statusConfig: Record<string, { color: string; label: string }> = {
  SCHEDULED: { color: "bg-gray-100 text-gray-700", label: "Scheduled" },
  ASSIGNED: { color: "bg-blue-100 text-blue-700", label: "Assigned" },
  EN_ROUTE: { color: "bg-cyan-100 text-cyan-700", label: "En Route" },
  ARRIVED: { color: "bg-teal-100 text-teal-700", label: "Arrived" },
  COLLECTED: { color: "bg-emerald-100 text-emerald-700", label: "Collected" },
  HANDED_TO_WORKSHOP: { color: "bg-green-100 text-green-700", label: "At Workshop" },
  OUT_FOR_DELIVERY: { color: "bg-orange-100 text-orange-700", label: "Out for Delivery" },
  DELIVERED: { color: "bg-green-100 text-green-700", label: "Delivered" },
  FAILED_ATTEMPT: { color: "bg-red-100 text-red-700", label: "Failed" },
  RESCHEDULED: { color: "bg-yellow-100 text-yellow-700", label: "Rescheduled" },
  CANCELLED: { color: "bg-red-100 text-red-700", label: "Cancelled" },
};

export function ShipmentCard({
  shipment,
  onClaim,
  onOpen,
  isClaiming,
}: ShipmentCardProps) {
  const type = typeConfig[shipment.type] || typeConfig.PICKUP;
  const status = statusConfig[shipment.status] || statusConfig.SCHEDULED;
  const TypeIcon = type.icon;

  const mapsUrl = shipment.address.lat && shipment.address.lng
    ? `https://www.google.com/maps/search/?api=1&query=${shipment.address.lat},${shipment.address.lng}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        `${shipment.address.line1}, ${shipment.address.city}, ${shipment.address.pincode}`
      )}`;

  const isUnassigned = !shipment.assignedRider;

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4 flex-1 min-w-0">
            <div className={`p-2.5 rounded-lg ${type.color}`}>
              <TypeIcon className="h-5 w-5" />
            </div>

            <div className="flex-1 min-w-0 space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={type.color}>{type.label}</Badge>
                <Badge className={status.color}>{status.label}</Badge>
                {shipment.otpVerifiedAt && (
                  <Badge className="bg-emerald-100 text-emerald-700 gap-1">
                    <ShieldCheck className="h-3 w-3" />
                    OTP Verified
                  </Badge>
                )}
              </div>

              <div className="grid gap-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Clock className="h-4 w-4 shrink-0" />
                  <span>
                    {formatTimeRange(shipment.slot.startAt, shipment.slot.endAt)}
                  </span>
                </div>

                <div className="flex items-center gap-2 text-muted-foreground">
                  <Package className="h-4 w-4 shrink-0" />
                  <span>
                    {shipment.order.publicId} - {shipment.order.itemCount} items
                  </span>
                </div>

                <div className="flex items-center gap-2 text-muted-foreground">
                  <User className="h-4 w-4 shrink-0" />
                  <span className="truncate">{shipment.customer.name}</span>
                </div>
              </div>

              <div className="flex items-start gap-2 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="truncate">{shipment.address.line1}</p>
                  <p className="truncate">
                    {shipment.address.city} - {shipment.address.pincode}
                  </p>
                  <p className="text-xs">{shipment.area.name}</p>
                </div>
                <a
                  href={mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 text-primary hover:underline flex items-center gap-1"
                >
                  <ExternalLink className="h-3 w-3" />
                  Maps
                </a>
              </div>

              {shipment.customer.phone && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Phone className="h-4 w-4 shrink-0" />
                  <a
                    href={`tel:${shipment.customer.phone}`}
                    className="text-primary hover:underline"
                  >
                    {shipment.customer.phone}
                  </a>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2 shrink-0">
            {isUnassigned && onClaim && (
              <Button size="sm" onClick={onClaim} disabled={isClaiming}>
                <Hand className="h-4 w-4 mr-1" />
                {isClaiming ? "Claiming..." : "Claim"}
              </Button>
            )}
            {onOpen && (
              <Button variant="outline" size="sm" onClick={onOpen}>
                <Eye className="h-4 w-4 mr-1" />
                Open
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
